import React, { useState, useEffect } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { Table, TableBody, TableHead, TableCell, TableContainer, TableRow, Paper, Switch } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { updateLoading } from '../../../redux/features/common/commonSlice';
import ApiCaller from '../../constants/ApiCaller';
import { showToast, showDangerToast } from '../../constants/toaster';
import NavBar from '../../structure/NavBar';
import { NavBarTop } from '../../structure/NavBarTop';
import urls from '../../constants/apiurl';

const NewsFeedsList = () => {
    const [newsFeeds, setNewsFeeds] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        category: '',
        publishDate: '',
        authorName: '',
        status: 'Draft',
        isFeatured: false,
    });
    const [formErrors, setFormErrors] = useState({});
    const [isFormValid, setIsFormValid] = useState(false); // New state to track form validity
    const dispatch = useDispatch();
    const userData = useSelector(state => state?.userData);

    // Fetch news feeds list
    const fetchNewsFeeds = async () => {
        try {
            dispatch(updateLoading(true));
            const path = urls.newsFeedList;
            const res = await ApiCaller.get({}, path, userData?.access_token);
            const response = await res.response.json();
            if (response.status === 200) {
                setNewsFeeds(response.data);
            } else {
                showDangerToast(response.message);
            }
        } catch (error) {
            console.error(error);
            showDangerToast(error.message);
        } finally {
            dispatch(updateLoading(false));
        }
    };

    // Validate form fields
    const validateForm = () => {
        const errors = {};
        if (!formData.title.trim()) errors.title = 'Title is required.';
        if (!formData.content.trim()) errors.content = 'Content is required.';
        if (!formData.category.trim()) errors.category = 'Category is required.';
        if (!formData.publishDate.trim()) errors.publishDate = 'Publish Date is required.';
        if (!formData.authorName.trim()) errors.authorName = 'Author Name is required.';
        setFormErrors(errors);
        setIsFormValid(Object.keys(errors).length === 0); // Update form validity state
    };

    // Trigger validation whenever formData changes
    useEffect(() => {
        validateForm();
    }, [formData]);

    // Handle add/edit form submission
    const handleSubmit = async () => {
        if (!isFormValid) return;

        try {
            dispatch(updateLoading(true));
            const path = formData.id ? `/api/news-feeds/${formData.id}` : '/api/news-feeds';
            const method = formData.id ? 'PUT' : 'POST';
            const res = await ApiCaller[method](formData, path, userData?.access_token);
            const response = await res.response.json();
            if (response.status === 200) {
                showToast(response.message);
                fetchNewsFeeds();
                setShowModal(false);
            } else {
                showDangerToast(response.message);
            }
        } catch (error) {
            console.error(error);
            showDangerToast(error.message);
        } finally {
            dispatch(updateLoading(false));
        }
    };

    // Handle delete
    const handleDelete = async (id) => {
        try {
            dispatch(updateLoading(true));
            const path = `/api/news-feeds/${id}`;
            const res = await ApiCaller.delete({}, path, userData?.access_token);
            const response = await res.response.json();
            if (response.status === 200) {
                showToast(response.message);
                fetchNewsFeeds();
            } else {
                showDangerToast(response.message);
            }
        } catch (error) {
            console.error(error);
            showDangerToast(error.message);
        } finally {
            dispatch(updateLoading(false));
        }
    };

    // Handle status change
    const handleStatusChange = async (id, status) => {
        try {
            dispatch(updateLoading(true));
            const path = `/api/news-feeds/${id}/status`;
            const res = await ApiCaller.put({ status }, path, userData?.access_token);
            const response = await res.response.json();
            if (response.status === 200) {
                showToast(response.message);
                fetchNewsFeeds();
            } else {
                showDangerToast(response.message);
            }
        } catch (error) {
            console.error(error);
            showDangerToast(error.message);
        } finally {
            dispatch(updateLoading(false));
        }
    };

    useEffect(() => {
        fetchNewsFeeds();
    }, []);

    return (
        <>
            <NavBar />
            <div className="main-content">
                <NavBarTop />
                <div className="header">
                    <div className="logo">
                        <div className="header bg-gradient-primary pb-8 pt-5 pt-md-8"></div>
                    </div>
                </div>
                <div className="container-fluid mt--7">
                    <div className="row">
                        <div className="col text-left">
                            <div className="card shadow">
                                <div className="card-header border-0">
                                    <div className="row align-items-center text-left">
                                        <div className="col">
                                            <h3 className="mb-0">News Feeds</h3>
                                        </div>
                                        <div className="col text-right">
                                            <Button
                                                className="btn btn-sm btn-primary text-white"
                                                onClick={() => {
                                                    setFormData({
                                                        title: '',
                                                        content: '',
                                                        category: '',
                                                        publishDate: '',
                                                        authorName: '',
                                                        status: 'Draft',
                                                        isFeatured: false,
                                                    });
                                                    setFormErrors({});
                                                    setShowModal(true);
                                                }}
                                            >
                                                Add News Feed
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <TableContainer component={Paper}>
                                        <Table aria-label="news feeds table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Title</TableCell>
                                                    <TableCell>Category</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Publish Date</TableCell>
                                                    <TableCell>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {newsFeeds.map((feed) => (
                                                    <TableRow key={feed.id}>
                                                        <TableCell>{feed.title}</TableCell>
                                                        <TableCell>{feed.category}</TableCell>
                                                        <TableCell>
                                                            <Switch
                                                                checked={feed.status === 'Published'}
                                                                onChange={(e) =>
                                                                    handleStatusChange(feed.id, e.target.checked ? 'Published' : 'Draft')
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>{feed.publishDate}</TableCell>
                                                        <TableCell>
                                                            <Button
                                                                variant="outlined"
                                                                onClick={() => {
                                                                    setFormData(feed);
                                                                    setFormErrors({});
                                                                    setShowModal(true);
                                                                }}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                variant="outlined"
                                                                color="secondary"
                                                                onClick={() => handleDelete(feed.id)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{formData.id ? 'Edit News Feed' : 'Add News Feed'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group>
                                <Form.Label>Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    isInvalid={!!formErrors.title}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.title}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Content</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={5}
                                    value={formData.content}
                                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                    isInvalid={!!formErrors.content}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.content}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Category</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    isInvalid={!!formErrors.category}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.category}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Publish Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.publishDate}
                                    onChange={(e) => setFormData({ ...formData, publishDate: e.target.value })}
                                    isInvalid={!!formErrors.publishDate}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.publishDate}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Author Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.authorName}
                                    onChange={(e) => setFormData({ ...formData, authorName: e.target.value })}
                                    isInvalid={!!formErrors.authorName}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.authorName}</Form.Control.Feedback>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={handleSubmit} disabled={!isFormValid}>
                            Save
                        </Button>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </>
    );
};

export default NewsFeedsList;